import { useLayoutEffect, useRef } from "react"

const FOCUSABLE_CONTENT = [
  "a[href]",
  "area[href]",
  "button",
  "input",
  "select",
  "textarea",
  "summary",
  "iframe",
  "object",
  "embed",
  "audio[controls]",
  "video[controls]",
  "[contenteditable]:not([contenteditable='false'])",
  "[tabindex]"
].join(",")

/** Skip the thumbnail's controls in Tab order while retaining readable content. */
export default function usePreviewContentRef(skipTabStops: boolean) {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const container = ref.current
    if (!skipTabStops || !container) return

    const originalTabIndexes = new Map<HTMLElement, string | null>()
    const skipContent = () => {
      container
        .querySelectorAll<HTMLElement>(FOCUSABLE_CONTENT)
        .forEach((element) => {
          const tabIndex = element.getAttribute("tabindex")
          if (tabIndex !== null && Number(tabIndex) < 0) return
          originalTabIndexes.set(element, tabIndex)
          element.setAttribute("tabindex", "-1")
        })
    }

    skipContent()
    // Blocks and embedded players can load controls after the card mounts.
    const observer = new MutationObserver(skipContent)
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["tabindex", "href", "contenteditable", "controls"]
    })

    return () => {
      observer.disconnect()
      originalTabIndexes.forEach((tabIndex, element) => {
        if (element.getAttribute("tabindex") !== "-1") return
        if (tabIndex === null) element.removeAttribute("tabindex")
        else element.setAttribute("tabindex", tabIndex)
      })
    }
  }, [skipTabStops])

  return ref
}
